import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import config from '../config';

interface InvoiceItem {
  id: string;
  description: string;
  cost: string;
  order: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  contractor_name: string;
  property_address: string;
  normalized_address: string;
  flat_label?: string;
  subtotal: string;
  tax: string;
  tax_type?: string;
  total: string;
  pdf_filename: string;
  original_filename: string;
  items: InvoiceItem[];
  created_at: string;
  bank_transaction_id?: string | null;
  bank_statement_transaction_id?: string | null;
  confirmed?: boolean;
  excluded_from_transaction_list?: boolean;
}

interface InvoiceCardProps {
  invoice: Invoice;
  onUpdate: () => void;
  onDelete: (invoiceId: string) => void;
  /** Opens the "is this a new expense, or proof of an existing transaction?" dialog for this
   * invoice — only relevant while confirmed === false (see Invoice.confirmed). */
  onReview?: (invoice: Invoice) => void;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onUpdate, onDelete, onReview }) => {
  const [editOpen, setEditOpen] = useState(false);
  const [descDialog, setDescDialog] = useState<{ open: boolean; description: string; label: string }>({ open: false, description: '', label: '' });
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [unlinkProofConfirmOpen, setUnlinkProofConfirmOpen] = useState(false);
  const [unlinkingProof, setUnlinkingProof] = useState(false);
  const [unlinkProofError, setUnlinkProofError] = useState<string | null>(null);
  
  // Sum of the actual line items (excluding tax/VAT) — shown as "Subtotal" on the card, and used
  // by the mismatch check below.
  const itemsSum = (invoice.items || []).reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);

  // Effective tax rate shown next to the tax label, e.g. "VAT (20%)" — derived from tax ÷
  // subtotal rather than trusted as its own extracted field, since invoices don't always print
  // the rate explicitly and this keeps it consistent with the amounts actually shown. null when
  // there's no subtotal to divide by (avoids a NaN/Infinity% flashing before data loads).
  const taxRatePercent = (() => {
    const tax = parseFloat(invoice.tax) || 0;
    if (itemsSum <= 0) return null;
    const rate = (tax / itemsSum) * 100;
    return Math.round(rate * 10) / 10; // one decimal place, e.g. 17.5
  })();

  // Check if total matches sum of line items — with no items at all there's nothing to compare
  // against (itemsSum would trivially be 0), so that's "no line items extracted", not a mismatch.
  // Line items are usually pre-tax, so `total` (which includes tax/VAT) is compared against
  // itemsSum + tax. But some invoices itemize VAT as its own line, in which case itemsSum already
  // equals total on its own — adding tax again would double-count it and falsely flag a real
  // match as a mismatch, so it's only flagged if BOTH reconciliations fail.
  const checkTotalMismatch = () => {
    if (!invoice.items || invoice.items.length === 0) return false;
    const tax = parseFloat(invoice.tax) || 0;
    const total = parseFloat(invoice.total) || 0;
    return Math.abs(itemsSum - total) > 0.01 && Math.abs((itemsSum + tax) - total) > 0.01; // Allow for small floating point differences
  };
  
  const hasTotalMismatch = checkTotalMismatch();
  
  // Edit form state
  const [editData, setEditData] = useState({
    invoice_date: invoice.invoice_date,
    contractor_name: invoice.contractor_name,
    property_address: invoice.property_address,
    normalized_address: invoice.normalized_address,
    flat_label: invoice.flat_label || '',
    subtotal: invoice.subtotal,
    tax: invoice.tax,
    tax_type: invoice.tax_type || 'Tax',
    total: invoice.total,
    items: invoice.items
  });

  const handleView = () => {
    // Open the invoice in a new tab (displays inline, not download)
    const viewUrl = `${config.apiUrl}/api/invoice-view/${invoice.id}`;
    window.open(viewUrl, '_blank');
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // The backend always streams the raw file directly (send_file) — never a JSON
        // { download_url } response — so this must go straight to blob, not response.json()
        // (which was silently throwing on the binary body, swallowed by the catch below).
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = invoice.original_filename || invoice.pdf_filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const handleEdit = () => {
    setEditData({
      invoice_date: invoice.invoice_date,
      contractor_name: invoice.contractor_name,
      property_address: invoice.property_address,
      normalized_address: invoice.normalized_address,
      flat_label: invoice.flat_label || '',
      subtotal: invoice.subtotal,
      tax: invoice.tax,
      tax_type: invoice.tax_type || 'Tax',
      total: invoice.total,
      items: invoice.items
    });
    setEditOpen(true);
  };

  // Hides this "cash" entry from the merged bank "View transactions" list without deleting
  // anything. Only relevant for a confirmed, unlinked invoice that's currently showing there by
  // default — re-including it (see "Add to transaction" above) goes through the full link/add-as-
  // new choice again rather than a quiet toggle, since that's the decision being deferred.
  const handleHideFromTransactions = async () => {
    const token = localStorage.getItem('auth_token');
    await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/exclude-from-transactions`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ excluded: true }),
    });
    onUpdate();
  };

  // Unlinking an invoice attached as proof of a real bank transaction is destructive — see
  // ProofViewDialog's own handleUnlink (2026-08-12): it permanently deletes the invoice itself
  // (row, items, stored file), not just the link, so re-attaching the same file elsewhere never
  // hits a stale content_hash "already uploaded" 409. Mirrored here so this action is reachable
  // directly from the Invoices grid, not only from inside the bank transaction's own dialog.
  const handleUnlinkProof = async () => {
    setUnlinkingProof(true);
    setUnlinkProofError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/unlink-proof`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to unlink this invoice (${res.status})`);
      }
      setUnlinkProofConfirmOpen(false);
      onUpdate();
    } catch (err: any) {
      setUnlinkProofError(err.message || 'Failed to unlink this invoice');
    } finally {
      setUnlinkingProof(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        setEditOpen(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
    }
  };

  const handleItemChange = (index: number, field: 'description' | 'cost', value: string) => {
    const newItems = [...editData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditData({ ...editData, items: newItems });
  };

  const addItem = () => {
    const newItems = [...editData.items, {
      id: '',
      description: '',
      cost: '0.00',
      order: editData.items.length + 1
    }];
    setEditData({ ...editData, items: newItems });
  };

  const removeItem = (index: number) => {
    const newItems = editData.items.filter((_, i) => i !== index);
    setEditData({ ...editData, items: newItems });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === 'invoice_date_not_found') return 'Date not found';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value: string) => {
    if (!value || value === '0.00') return '£0.00';
    return `£${parseFloat(value).toFixed(2)}`;
  };

  return (
    <>
      <Card sx={{ 
        boxShadow: 1,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          boxShadow: 3
        },
        transition: 'box-shadow 0.3s',
        height: '100%'
      }}>
        <CardContent sx={{ p: 1.75 }}>
          <Box display="flex" gap={1.75}>
            {/* Left Side - Preview and Action Icons — sized down (30% on mobile, 20% on desktop) */}
            <Box sx={{ width: { xs: 92, sm: 106 }, flexShrink: 0 }}>
              {/* Invoice Preview */}
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleView();
                }}
                sx={{
                  width: { xs: 92, sm: 106 },
                  height: { xs: 117, sm: 134 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 1,
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <img
                  src={`${config.apiUrl}/api/invoices/${invoice.id}/preview?t=${invoice.created_at}`}
                  alt={`Invoice preview`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    // Fallback to receipt icon if preview fails
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLElement).parentElement;
                    if (parent && !parent.querySelector('.receipt-fallback')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'receipt-fallback';
                      fallback.style.cssText = `
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        background-color: #f5f5f5;
                        color: #666;
                      `;
                      fallback.innerHTML = `
                        <div style="text-align: center;">
                          <div style="font-size: 24px; margin-bottom: 4px;">🧾</div>
                          <div style="font-size: 10px;">Invoice</div>
                        </div>
                      `;
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </Box>

              {/* Action Icons Below Preview */}
              <Box display="flex" justifyContent="center" gap={1}>
                <Tooltip title={hasTotalMismatch ? "Edit invoice - Total mismatch detected!" : "Edit invoice"}>
                  <IconButton 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEdit();
                    }} 
                    size="small"
                    sx={{ 
                      backgroundColor: hasTotalMismatch ? 'warning.main' : 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: hasTotalMismatch ? 'warning.dark' : 'primary.dark' },
                      ...(hasTotalMismatch && {
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            boxShadow: '0 0 0 0 rgba(237, 108, 2, 0.7)',
                          },
                          '50%': {
                            boxShadow: '0 0 0 8px rgba(237, 108, 2, 0)',
                          },
                        },
                      })
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownload();
                    }} 
                    size="small"
                    sx={{ 
                      backgroundColor: 'success.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'success.dark' }
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete invoice">
                  <IconButton 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete(invoice.id);
                    }} 
                    size="small"
                    sx={{ 
                      backgroundColor: 'error.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'error.dark' }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right Side - Invoice Details */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Contractor Name, with date (and, if applicable, "Add to transaction") below it */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'text.primary'
                  }}
                >
                  {invoice.contractor_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {formatDate(invoice.invoice_date)}
                  </Typography>
                  {!invoice.bank_transaction_id && !invoice.bank_statement_transaction_id && invoice.confirmed !== false && invoice.excluded_from_transaction_list && onReview && (
                    <Button
                      size="small" variant="text" startIcon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReview(invoice); }}
                      sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                    >
                      Add to transaction
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Rendered for every state except the one already handled next to the date above
                  (excluded_from_transaction_list, "Add to transaction") — skipping the Box
                  entirely in that case avoids an empty row still taking up margin/space. */}
              {!(!invoice.bank_transaction_id && !invoice.bank_statement_transaction_id && invoice.confirmed !== false && invoice.excluded_from_transaction_list) && (
              <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {(invoice.bank_transaction_id || invoice.bank_statement_transaction_id) && (
                    <>
                      <Tooltip title="Attached as proof of a bank transaction">
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: '12px !important' }} />}
                          label="Linked"
                          size="small" color="success" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                      <Button
                        size="small" variant="text" startIcon={<UnlinkIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUnlinkProofError(null); setUnlinkProofConfirmOpen(true); }}
                        sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                      >
                        Unlink
                      </Button>
                    </>
                  )}
                  {invoice.confirmed === false && (
                    <>
                      <Tooltip title="Not counted in your totals yet">
                        <Chip
                          label="Pending"
                          size="small" color="warning" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                      {onReview && (
                        <Button
                          size="small" variant="text" startIcon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReview(invoice); }}
                          sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                        >
                          Link or confirm
                        </Button>
                      )}
                    </>
                  )}
                  {!invoice.bank_transaction_id && !invoice.bank_statement_transaction_id && invoice.confirmed !== false && !invoice.excluded_from_transaction_list && (
                    <>
                      <Tooltip title="Showing as its own entry in the bank 'View transactions' list">
                        <Chip
                          label="Added to transactions"
                          size="small" color="success" variant="outlined"
                          sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                      <Button
                        size="small" variant="text" startIcon={<UnlinkIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleHideFromTransactions(); }}
                        sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: 'none' }}
                      >
                        Unlink
                      </Button>
                    </>
                  )}
              </Box>
              )}

              <Divider sx={{ my: 1.25 }} />

              {/* Subtotal — sum of the actual line items (excluding tax/VAT), with a link to see
                  each individual item's description + cost rather than listing them all inline. */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Typography
                  component="a"
                  variant="body2"
                  onClick={() => setItemsDialogOpen(true)}
                  sx={{ color: 'text.secondary', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.8rem' }}
                >
                  Subtotal &middot; See details
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', ml: 2, fontSize: '0.875rem', flexShrink: 0 }}>
                  {formatCurrency(itemsSum.toFixed(2))}
                </Typography>
              </Box>

              {/* Tax — labelled with whatever the invoice itself called it (VAT, Insurance Premium
                  Tax, etc.), falling back to the generic "Tax" if unknown, with the effective
                  rate computed from tax ÷ subtotal shown alongside it. */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  {invoice.tax_type || 'Tax'}{taxRatePercent !== null && ` (${taxRatePercent}%)`}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', ml: 2, fontSize: '0.875rem', flexShrink: 0 }}>
                  {formatCurrency(invoice.tax)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.25 }} />

              {/* Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                    Total
                  </Typography>
                  {hasTotalMismatch && (
                    <Tooltip title="Warning: Total doesn't match sum of line items. Click the yellow edit button to correct.">
                      <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: hasTotalMismatch ? 'warning.main' : 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}
                >
                  {formatCurrency(invoice.total)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Line item details — each individual item's description + cost */}
      <Dialog open={itemsDialogOpen} onClose={() => setItemsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Line Items
          <IconButton size="small" onClick={() => setItemsDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} divider={<Divider />}>
            {invoice.items.map((item, index) => (
              <Box key={item.id || index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.description || 'No description available.'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                  {formatCurrency(item.cost)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Description Popup */}
      <Dialog open={descDialog.open} onClose={() => setDescDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Description
          <IconButton size="small" onClick={() => setDescDialog(d => ({ ...d, open: false }))}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {descDialog.description || 'No description available.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescDialog(d => ({ ...d, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editOpen} 
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {hasTotalMismatch && (
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'warning.light', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <WarningIcon sx={{ color: 'warning.dark' }} />
                <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                  <strong>Warning:</strong> The total amount doesn't match the sum of line items. Please verify and correct the amounts below.
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Invoice Date (YYYY-MM-DD)"
                value={editData.invoice_date}
                onChange={(e) => setEditData({ ...editData, invoice_date: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Contractor Name"
                value={editData.contractor_name}
                onChange={(e) => setEditData({ ...editData, contractor_name: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>

            <Divider sx={{ my: 1 }} />
            <TextField
              label="Flat Label"
              value={editData.flat_label}
              onChange={(e) => setEditData({ ...editData, flat_label: e.target.value })}
              fullWidth
            />
            <TextField
              label="Property Group"
              value={editData.normalized_address}
              onChange={(e) => setEditData({ ...editData, normalized_address: e.target.value })}
              fullWidth
            />
            <TextField
              label="Full Address"
              value={editData.property_address}
              onChange={(e) => setEditData({ ...editData, property_address: e.target.value })}
              fullWidth
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Line Items
            </Typography>
            
            {editData.items.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Description"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Cost"
                  value={item.cost}
                  onChange={(e) => handleItemChange(index, 'cost', e.target.value)}
                  sx={{ width: '150px' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton onClick={() => removeItem(index)} color="error" size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={addItem} color="primary" size="small">
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            
            {editData.items.length === 0 && (
              <Button onClick={addItem} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
                Add Item
              </Button>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Subtotal"
                value={editData.subtotal}
                onChange={(e) => setEditData({ ...editData, subtotal: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Tax Type"
                value={editData.tax_type}
                onChange={(e) => setEditData({ ...editData, tax_type: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Tax Amount"
                value={editData.tax}
                onChange={(e) => setEditData({ ...editData, tax: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Total"
                value={editData.total}
                onChange={(e) => setEditData({ ...editData, total: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm unlinking a proof-linked invoice — mirrors ProofViewDialog's own confirmation,
          since this permanently deletes the invoice (not just the link) — see handleUnlinkProof. */}
      <Dialog open={unlinkProofConfirmOpen} onClose={() => !unlinkingProof && setUnlinkProofConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink and delete this invoice?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes it from the transaction and permanently deletes the invoice itself — the file, its details, and its line items. This can't be undone.
          </Typography>
          {unlinkProofError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUnlinkProofError(null)}>
              {unlinkProofError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkProofConfirmOpen(false)} disabled={unlinkingProof}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={handleUnlinkProof} disabled={unlinkingProof}
            startIcon={unlinkingProof ? <CircularProgress size={16} /> : undefined}
          >
            {unlinkingProof ? 'Deleting…' : 'Unlink and delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceCard;
